/**
 * Detailed Process data for Quantum Star Marketing.
 *
 * Mirrors the 6-step engagement model shown in the Process section.
 * Examples are drawn from active and recently-completed UAE/GCC engagements.
 */
const PROCESS_STEPS = [
  {
    key: "01",
    n: "01",
    eyebrow: "How we operate",
    name: "Discovery",
    tagline: "Deep-dive into your business, ICP, offer economics and current funnel.",
    overview:
      "Most agencies skip this step and pay for it later. We don't. The first 7-10 days are about understanding your business at the depth of a co-founder — not a vendor. We sit with leadership, study your unit economics, interview your best customers, and pressure-test your offer. Nothing else we ship will be worth your money if the discovery is shallow.",
    highlights: [
      "Founder + leadership working session (3–4 hours, in person or remote)",
      "ICP definition workshop with named customer archetypes",
      "Unit-economics teardown (CAC, LTV, payback, gross margin)",
      "5–8 customer interviews (current, churned, ideal prospects)",
      "Competitor + category landscape mapping",
      "Offer-economics pressure test (is the price right? is the promise tight?)",
      "Written discovery report delivered before strategy phase",
    ],
    highlightsLabel: "What happens in discovery",
    phases: [
      { day: "Day 1–3", body: "Kickoff workshop + access to your data + first customer interviews." },
      { day: "Day 4–7", body: "Interviews complete, unit economics modelled, gaps mapped." },
      { day: "Day 8–10", body: "Discovery report delivered. Sign-off before strategy begins." },
    ],
    phasesLabel: "Days 1–10",
    outcomes: [
      "Shared understanding of where money is leaking",
      "Written ICP definition + buying motion documented",
      "Honest read on whether your offer can scale (or needs fixing first)",
      "A baseline every later decision will be tested against",
    ],
    outcomesLabel: "What you leave with",
    examples: [
      "Dubai real estate brokerage — discovery revealed their best leads came from WhatsApp groups, not portals",
      "MENA SaaS — discovery surfaced 4 ICPs they were marketing to; only 1 was profitable",
      "UAE DTC fragrance — found 38% of revenue came from 6% of customers (now their LTV focus)",
    ],
    examplesLabel: "Examples from past engagements",
  },
  {
    key: "02",
    n: "02",
    eyebrow: "How we operate",
    name: "Audit",
    tagline: "Forensic audit across SEO, paid, creative, tech, analytics and conversion.",
    overview:
      "We forensically examine every growth lever you're currently running — paid accounts (Meta, Google, TikTok), SEO, technical site health, conversion funnels, analytics tracking, creative library, retention flows. We benchmark each against industry-leading thresholds and rank them by 'biggest revenue unlock per week of effort'. This is how we know where to start.",
    highlights: [
      "Paid accounts: structure, spend distribution, attribution, creative",
      "SEO: technical, on-page, off-page, content gaps, EN/AR coverage",
      "Site: Lighthouse, Core Web Vitals, mobile UX, conversion paths",
      "Analytics: GA4 + GTM + CRM-revenue tracking validity",
      "Creative library: hook quality, format mix, ad fatigue",
      "Retention: lifecycle flows, ESP setup, segmentation quality",
      "Forensic report scored across 60+ dimensions",
    ],
    highlightsLabel: "Audit scope",
    phases: [
      { day: "Day 11–14", body: "All accounts + data sources connected for read access." },
      { day: "Day 15–18", body: "Audit completed across 60+ dimensions, scored 1–5 each." },
      { day: "Day 19–21", body: "Audit report + prioritised opportunity stack delivered." },
    ],
    phasesLabel: "Days 11–21",
    outcomes: [
      "Honest scorecard across every growth lever (no salesman gloss)",
      "Top-10 prioritised opportunities ranked by revenue impact ÷ effort",
      "Quick-wins identified for immediate sprint (often 5–10 day fixes)",
      "Strategic gaps documented for the 90-day roadmap",
    ],
    outcomesLabel: "Audit outputs",
    examples: [
      "UAE healthcare clinic — audit found AED 38K/mo wasted on broad-match Google keywords",
      "GCC ecom — discovered Klaviyo flows hadn't fired in 8 months due to broken trigger",
      "B2B SaaS — found GA4 was double-counting demo bookings, inflating reported pipeline by 41%",
    ],
    examplesLabel: "Audit findings — real examples",
  },
  {
    key: "03",
    n: "03",
    eyebrow: "How we operate",
    name: "Strategy",
    tagline: "A 90-day growth blueprint mapped to revenue, not impressions.",
    overview:
      "Discovery + audit feed into a single document: a 90-day strategy mapped to revenue outcomes. Every initiative is sized (expected impact, required effort, week-by-week ownership), sequenced (what unlocks what), and tied to a real number on your P&L. No vague 'increase engagement' goals. Every line item answers: how does this earn back its cost?",
    highlights: [
      "90-day blueprint document (typically 40–60 pages)",
      "Initiatives sized by impact ÷ effort, ranked + sequenced",
      "Week-by-week shipping schedule with named owners",
      "Forecast: spend, revenue, ROAS, CAC across the 90 days",
      "Risk register: what could go wrong + mitigations",
      "Reporting structure + dashboards defined",
      "Leadership sign-off before execution begins",
    ],
    highlightsLabel: "What's in the blueprint",
    phases: [
      { day: "Day 22–25", body: "Strategy workshop with your leadership team." },
      { day: "Day 26–29", body: "Blueprint drafted, sized, sequenced." },
      { day: "Day 30", body: "Sign-off meeting. Strategy locked. Execution starts day 31." },
    ],
    phasesLabel: "Days 22–30",
    outcomes: [
      "One source of truth for every growth decision for 90 days",
      "Forecasts you can defend to a board or investor",
      "Clear sequencing — no 'try-everything-at-once' fragmentation",
      "Leadership alignment before the first AED is spent",
    ],
    outcomesLabel: "Why this matters",
    examples: [
      "MENA HRTech — strategy sequenced ABM before paid scale; cut CAC 41% before scale began",
      "UAE DTC brand — strategy chose to fix product page BEFORE adding spend; conversion 1.6 → 4.3%",
      "Dubai real estate group — strategy paused 3 of 5 channels to concentrate budget on winners",
    ],
    examplesLabel: "Strategic decisions — real examples",
  },
  {
    key: "04",
    n: "04",
    eyebrow: "How we operate",
    name: "Execution",
    tagline: "Cross-functional pod ships creative, campaigns, code and copy weekly.",
    overview:
      "From day 31 onwards, a dedicated pod ships work weekly. Your pod is a small senior team — strategist, paid buyer, SEO lead, creative producer, developer, retention lead — depending on scope. You get one point of contact, weekly performance reviews, and a real-time dashboard. No account-manager telephone game. No quarterly 'wait until the next report'.",
    highlights: [
      "Dedicated cross-functional pod (3–6 senior operators)",
      "Single point of contact + weekly performance review",
      "Real-time dashboard you can open at any moment",
      "Weekly ship cadence — something tangible goes live every week",
      "Slack channel with your team for daily collaboration",
      "Monthly executive deck for board / investor review",
      "Documented change log of every test, every campaign, every result",
    ],
    highlightsLabel: "How execution works",
    phases: [
      { day: "Week 5–6", body: "First sprint live: quick-wins + foundational fixes ship." },
      { day: "Week 7–10", body: "Channel campaigns scaled, creative engine producing weekly." },
      { day: "Week 11–13", body: "Compounding — winning experiments scaled, losers killed." },
    ],
    phasesLabel: "Weeks 5–13 cadence",
    outcomes: [
      "Visible weekly progress — no 'wait three months for results'",
      "Decisions made on Mondays, not next-quarter",
      "Your team learns alongside us — knowledge transfer baked in",
      "Documented playbook you keep even if engagement ends",
    ],
    outcomesLabel: "What execution looks like",
    examples: [
      "UAE DTC brand — shipped 124 ad variants, 22 landing page tests, 8 lifecycle flows in 90 days",
      "GCC SaaS — built 1,400 programmatic pages in 6 weeks, ranking on 380 of them by week 12",
      "MENA aesthetics chain — launched WhatsApp booking, AI chatbot, 6 new campaigns in 60 days",
    ],
    examplesLabel: "Execution velocity — real examples",
  },
  {
    key: "05",
    n: "05",
    eyebrow: "How we operate",
    name: "Optimization",
    tagline: "AI + analyst-led tuning across bids, creative, funnels and lifecycle.",
    overview:
      "Once campaigns are live, the work shifts from 'build' to 'tune'. We deploy AI bid + budget optimisation layered with weekly human analyst review. Creative is rotated before fatigue spikes CPMs. Landing pages are A/B tested. Lifecycle flows are tightened on real data. Every channel is squeezed for marginal efficiency — small daily wins compound into outsized monthly outcomes.",
    highlights: [
      "AI bid + budget layer running hourly across all paid channels",
      "Creative fatigue detection — auto-rotation before CPMs spike",
      "Landing page A/B testing with statistical confidence",
      "Lifecycle flow refinement based on opens + clicks + revenue",
      "Cross-channel budget reallocation by marginal ROAS",
      "Weekly analyst review on top of AI decisions",
      "Anomaly alerts pushed to Slack / WhatsApp",
    ],
    highlightsLabel: "Ongoing tuning levers",
    phases: [
      { day: "Weekly", body: "Performance review + budget reallocation across channels." },
      { day: "Bi-weekly", body: "Creative + landing page A/B test results reviewed + scaled." },
      { day: "Monthly", body: "Strategy revisit — are we still working the right priorities?" },
    ],
    phasesLabel: "Optimisation cadence",
    outcomes: [
      "Blended ROAS climbs 15–40% over the first 90 days post-launch",
      "Wasted spend cut to under 10% of total",
      "Creative testing velocity 3–5x agency average",
      "Anomalies caught in hours, not at month-end",
    ],
    outcomesLabel: "Outcomes of optimisation",
    examples: [
      "UAE DTC fragrance — AI layer caught a 14% CPM spike at 4am Sunday, paused 3 ad sets, saved AED 32K",
      "GCC SaaS — creative fatigue detection identified the winning hook was dying; replacement scaled to 6.4x ROAS",
      "MENA fintech — A/B test on demo page lifted booking rate 31% (now baseline)",
    ],
    examplesLabel: "Optimisation wins — real examples",
  },
  {
    key: "06",
    n: "06",
    eyebrow: "How we operate",
    name: "Scaling",
    tagline: "Compounding revenue with new channels, geos and AI-driven LTV plays.",
    overview:
      "By month 4-6 the foundation is stable, the winners are known, the team is in rhythm. Now we scale — across budget (4-6x prior spend), across channels (add TikTok, YouTube, programmatic, podcasts), across geographies (UAE → KSA → wider GCC → UK / US), and across LTV (subscriptions, upsells, win-back flows). This is where engagements typically 5-10x in impact.",
    highlights: [
      "Budget scale on proven winners — 4–6x prior spend",
      "New channel expansion (TikTok, YouTube, programmatic, OOH, podcasts)",
      "Geographic expansion — UAE → KSA → wider GCC → UK / US",
      "LTV plays: subscriptions, upsells, win-back, referral programmes",
      "Influencer + creator partnerships layered on top",
      "PR + earned media to amplify performance media",
      "Quarterly strategic refresh + new growth bets",
    ],
    highlightsLabel: "Scale levers we deploy",
    phases: [
      { day: "Month 4–6", body: "Budget scale + first new-channel + new-geo experiments." },
      { day: "Month 7–9", body: "LTV plays + influencer + earned media layered in." },
      { day: "Month 10–12", body: "Compounding — typical 5–10x impact vs month-1 baseline." },
    ],
    phasesLabel: "Scale timeline",
    outcomes: [
      "Revenue 5–10x'd vs engagement start (typical)",
      "Cost of growth declining as compounding kicks in",
      "New geos and channels de-risked + producing",
      "LTV up 30–80% via lifecycle + retention layer",
    ],
    outcomesLabel: "What scaling looks like",
    examples: [
      "UAE skincare — month 1 at AED 80K → month 11 at AED 720K monthly revenue",
      "MENA SaaS — KSA-only at start → UAE + Egypt + Oman live by month 9",
      "GCC F&B brand — added subscription model month 5; LTV up 184% by month 12",
    ],
    examplesLabel: "Scaling outcomes — real examples",
  },
];

export default PROCESS_STEPS;
